import type { VocabRow } from './settings-api';

/**
 * Reordering, as pure functions over a list.
 *
 * ⭐ Kept out of the component because "did the drag put it in the right place" is a question with an
 * exact answer, and answering it in a DOM test means simulating pointer events to assert arithmetic.
 *
 * ⛔ NOTHING HERE MUTATES. `move` returns a new array; the caller replaces its state with it. The
 * house rule, and here it also matters practically: the pending order and the last-saved order are
 * compared to decide which rows need a PATCH, and an in-place splice would make the two the same
 * object and the diff empty.
 */

export function move<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to) return [...items];
  if (from < 0 || from >= items.length) return [...items];
  // ⚠️ Clamp rather than refuse: "drag past the end" is a real gesture and its intent is "to the end".
  const target = Math.max(0, Math.min(items.length - 1, to));
  const next = [...items];
  const [lifted] = next.splice(from, 1);
  if (lifted === undefined) return [...items];
  next.splice(target, 0, lifted);
  return next;
}

export function indexOfCode(items: readonly VocabRow[], code: string): number {
  return items.findIndex((row) => row.code === code);
}

/** Move by code — what a keyboard "move up" button and a drop both actually mean. */
export function moveCode(items: readonly VocabRow[], code: string, delta: number): VocabRow[] {
  const from = indexOfCode(items, code);
  if (from < 0) return [...items];
  return move(items, from, from + delta);
}

export interface OrderChange {
  code: string;
  sort_order: number;
}

/**
 * The PATCHes a reorder implies.
 *
 * 🔴 SORT ORDERS ARE RE-SPACED BY TENS, not renumbered 1..n. The seed uses 10, 20, 30… (db/001) and
 * db/005 says in as many words that it "re-spaced" two rows so a new one could be inserted between
 * them. Renumbering to consecutive integers would work once and leave no room for the next insert,
 * which is how a list ends up needing a full rewrite to add one row in the middle.
 *
 * ⭐ Only CHANGED rows are returned. Dragging row 26 to position 25 must not send 26 PATCHes — every
 * one of them is a chance for a partial failure, and a partial failure of a renumber leaves the list
 * in an order nobody chose.
 */
export function orderChanges(
  previous: readonly VocabRow[],
  next: readonly VocabRow[],
): OrderChange[] {
  const before = new Map(previous.map((row) => [row.code, row.sort_order]));
  const changes: OrderChange[] = [];

  next.forEach((row, index) => {
    const sort_order = (index + 1) * 10;
    if (before.get(row.code) !== sort_order) changes.push({ code: row.code, sort_order });
  });

  return changes;
}

/**
 * ⚠️ The list is rendered in the SERVER's order (`ORDER BY sort_order`) and this only re-derives it
 * after a local move. ⛔ It is not a sort applied on load: two rows sharing a sort_order have an
 * order the server chose, and re-sorting client-side would shuffle them on every render.
 */
export function hasPendingOrder(previous: readonly VocabRow[], next: readonly VocabRow[]): boolean {
  if (previous.length !== next.length) return true;
  return previous.some((row, index) => row.code !== next[index]?.code);
}
